// ============================================
// GARANTIAPUBLICA.JS — pagina PUBLICA del certificado de garantia (QR) — /garantia/:codigo
// ============================================
// Sin login. Solo lectura. Muestra la version OFICIAL que vive en el sistema (CAPA 2):
// cliente, productos/servicios con su garantia, inicio, vencimiento y estado (vigente /
// por vencer / vencida). SIN precios ni datos sensibles (el backend ya no los manda).
// Responsive: una columna en celular, tarjetas con tabla en scroll propio.

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ShieldCheck, ShieldAlert, Clock } from 'lucide-react';
import { fmtFechaCorta, fmtGarantiaTexto } from './components/GarantiasLista';
import { API_BASE } from './config/api';

const API = API_BASE;   // direccion del backend: config/api.js (fuente unica)
const AZUL = '#265F97', AZUL_CLARO = '#DEEBF7', GRIS = '#3D4248', GRIS_CLARO = '#5E666E', BORDE = '#D7D9DB';
const ESTADO = {
  pendiente_entrega: { label: 'Pendiente de entrega', bg: '#F3F5F8', color: '#5E666E', texto: 'La garantía inicia en la fecha de entrega del proyecto.' },
  vigente:           { label: 'Vigente',              bg: '#E8FAF5', color: '#16a34a', texto: 'Esta garantía está vigente.' },
  por_vencer:        { label: 'Por vencer',           bg: '#FFF6E5', color: '#9A5B00', texto: 'Alguna garantía vence en los próximos 30 días.' },
  vencida:           { label: 'Vencida',              bg: '#FDE8E8', color: '#dc2626', texto: 'Las garantías de este certificado ya vencieron.' },
};

export default function GarantiaPublica() {
  const { codigo } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.title = 'Certificado de garantía · Beep México';
    fetch(`${API}/api/garantias/publico/${encodeURIComponent(codigo || '')}`)
      .then(async r => { const j = await r.json().catch(() => ({})); if (!r.ok || !j.ok) throw new Error(j.error || 'No se pudo consultar el certificado'); return j; })
      .then(setData)
      .catch(e => setError(e.message));
  }, [codigo]);

  const wrap = { minHeight: '100vh', background: '#F3F5F8', fontFamily: '"Plus Jakarta Sans", system-ui, -apple-system, Segoe UI, Roboto, sans-serif', color: GRIS, padding: '16px 12px 40px' };
  const card = { background: '#fff', borderRadius: 14, border: `1px solid ${BORDE}`, padding: 18, boxShadow: '0 4px 14px rgba(38,95,151,0.06)' };
  const cont = { maxWidth: 760, margin: '0 auto', display: 'grid', gap: 14 };

  if (error) {
    return (
      <div style={wrap}><div style={cont}>
        <div style={{ ...card, textAlign: 'center', padding: 32 }}>
          <ShieldAlert size={40} color="#dc2626" />
          <div style={{ fontSize: 18, fontWeight: 700, marginTop: 10 }}>Certificado no encontrado</div>
          <div style={{ fontSize: 14, color: GRIS_CLARO, marginTop: 6, lineHeight: 1.5 }}>{error}. Verifique el código impreso en su certificado o comuníquese con Beep México.</div>
        </div>
      </div></div>
    );
  }
  if (!data) return <div style={wrap}><div style={{ ...cont, textAlign: 'center', color: GRIS_CLARO, paddingTop: 40 }}>Consultando el registro oficial…</div></div>;

  const c = data.certificado; const est = ESTADO[c.estado] || ESTADO.pendiente_entrega;
  return (
    <div style={wrap}>
      <div style={cont}>
        <div style={{ ...card, borderTop: `5px solid ${AZUL}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <img src="/Logo%20Horizontal%20Original.png" alt="Beep México" style={{ height: 34, maxWidth: 160, objectFit: 'contain' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
              <div style={{ fontSize: 11, color: GRIS_CLARO, fontWeight: 600, letterSpacing: 0.6 }}>CERTIFICADO DE GARANTÍA</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: AZUL }}>{c.folio}</div>
            </div>
          </div>
          <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ padding: '6px 14px', borderRadius: 99, background: est.bg, color: est.color, fontSize: 13, fontWeight: 700 }}>{est.label}</span>
            <span style={{ fontSize: 13, color: GRIS_CLARO }}>{est.texto}</span>
          </div>
          <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '8px 20px', fontSize: 14 }}>
            <div><div style={{ fontSize: 11, color: GRIS_CLARO, fontWeight: 600 }}>Cliente</div><div style={{ fontWeight: 600 }}>{c.cliente || '—'}{c.sucursal ? ` · ${c.sucursal}` : ''}</div></div>
            <div><div style={{ fontSize: 11, color: GRIS_CLARO, fontWeight: 600 }}>Referencia</div><div>Cotización {c.cotizacion || '—'}</div></div>
            <div><div style={{ fontSize: 11, color: GRIS_CLARO, fontWeight: 600 }}>Inicio de garantía</div><div>{c.fecha_inicio ? fmtFechaCorta(c.fecha_inicio) : 'Fecha de entrega'}</div></div>
            <div><div style={{ fontSize: 11, color: GRIS_CLARO, fontWeight: 600 }}>Código de verificación</div><div style={{ fontFamily: 'monospace', fontWeight: 700 }}>{c.codigo}</div></div>
          </div>
        </div>

        <div style={card}>
          <div style={{ fontSize: 15, fontWeight: 700, color: AZUL, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}><ShieldCheck size={18} /> Equipos y servicios amparados</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 480 }}>
              <thead>
                <tr style={{ background: AZUL_CLARO, color: AZUL, textAlign: 'left' }}>
                  <th style={{ padding: '8px' }}>Producto / servicio</th>
                  <th style={{ padding: '8px' }}>Cant.</th>
                  <th style={{ padding: '8px' }}>Garantía</th>
                  <th style={{ padding: '8px' }}>Vence</th>
                  <th style={{ padding: '8px' }}>Estado</th>
                </tr>
              </thead>
              <tbody>
                {data.lineas.map((l, i) => {
                  const el = ESTADO[l.estado] || ESTADO.pendiente_entrega;
                  const esServ = l.tipo === 'servicio' || l.tipo === 'cable';
                  return (
                    <tr key={i} style={{ borderTop: `1px solid ${BORDE}`, background: esServ ? '#EAF7F0' : 'transparent' }}>
                      <td style={{ padding: '8px' }}>
                        <div style={{ fontWeight: 600 }}>{l.producto}</div>
                        <div style={{ fontSize: 12, color: esServ ? '#1E7A4C' : AZUL, fontWeight: 600 }}>{esServ ? 'BEEP MÉXICO · Servicio' : [l.marca, l.modelo].filter(Boolean).join(' · ')}</div>
                      </td>
                      <td style={{ padding: '8px' }}>{l.cantidad}</td>
                      <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>{fmtGarantiaTexto(l.garantia_meses, l.garantia_dias)}<div style={{ fontSize: 11, color: GRIS_CLARO }}>{l.origen}</div></td>
                      <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>{l.fecha_vencimiento ? fmtFechaCorta(l.fecha_vencimiento) : 'desde la entrega'}</td>
                      <td style={{ padding: '8px' }}><span style={{ padding: '3px 9px', borderRadius: 99, background: el.bg, color: el.color, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>{el.label}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div style={{ ...card, background: '#EEF6FF', border: '1px solid #CFE3FF' }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <ShieldCheck size={20} color={AZUL} style={{ flexShrink: 0, marginTop: 2 }} />
            <div style={{ fontSize: 13, lineHeight: 1.55 }}>
              <strong>Este es el registro oficial de Beep México.</strong> Cualquier documento que difiera de esta información no es válido.
              {data.integro === false && <div style={{ color: '#dc2626', fontWeight: 700, marginTop: 6 }}>Atención: el código no corresponde al contenido registrado. Comuníquese con Beep México.</div>}
              {data.contacto && <div style={{ marginTop: 8, color: GRIS_CLARO }}>{data.contacto}</div>}
              <div style={{ marginTop: 8, color: GRIS_CLARO, fontSize: 12, display: 'flex', gap: 6, alignItems: 'center' }}><Clock size={13} /> Emitido el {fmtFechaCorta(c.emitido)} · {data.empresa?.nombre}{data.empresa?.telefono ? ` · ${data.empresa.telefono}` : ''}{data.empresa?.correo ? ` · ${data.empresa.correo}` : ''}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
